import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { AlertTriangle, Bookmark, Check, Copy, CornerUpLeft, Ellipsis, Forward, Languages, Pencil, Pin, PinOff, SmilePlus, Trash2, X } from "@fg/icons";
import { connectParityApi, normalizeUserId, type ChatMessage, type ChatReaction, type ChatScope } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ForwardMessageDialog } from "./ForwardMessageDialog";
import { useChatMessageAction } from "@/api/hooks";
import { fileMarkersOf, splitFileMarkers } from "@/lib/fileMarkers";
import { hasPollMarker } from "@/lib/pollMarkers";
import { splitImportance, wrapImportance } from "@/lib/importance";
import { cn } from "@/lib/utils";
import { MessageBody } from "./files/MessageBody";
import { RichInput } from "./rich/RichInput";
import type { RichEditorHandle } from "./rich/RichEditor";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

/**
 * The body of one DM / group-chat message with Teams-style actions: a hover
 * bar (quick reactions, plus Edit and Delete on your own messages), inline
 * editing, an "(edited)" mark, and reaction chips that toggle on click.
 * Place it inside an element with the `group` class so the bar shows on hover.
 */
export function ChatMessageContent({
    scope,
    containerId,
    message,
    reactions,
    className,
    bubbleClassName,
    pin
}: {
    scope: ChatScope;
    containerId: string;
    message: { id: string; bodyHtml: string; authorUserId: string; isEdited?: boolean; sentAt?: string; authorEmail?: string; authorDisplayName?: string | null };
    reactions: ChatReaction[] | undefined;
    className?: string;
    /** Classes for the bubble around the text; reaction pills then sit under the bubble. */
    bubbleClassName?: string;
    /** Pinning (1:1 and group chats): whether it's pinned and how to toggle it. No pin action without it. */
    pin?: { pinned: boolean; onToggle: () => void };
}) {
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const mine = me !== "" && normalizeUserId(message.authorUserId) === me;
    // A poll message isn't edited — the poll card is the content.
    const editable = mine && !hasPollMarker(message.bodyHtml);
    const action = useChatMessageAction(scope, containerId);
    const [editing, setEditing] = useState(false);
    // The edit box is the rich composer, loaded with the message's HTML (its files stay out of it and are put back on save).
    const [editHtml, setEditHtml] = useState("");
    const [editEmpty, setEditEmpty] = useState(false);
    const editor = useRef<RichEditorHandle>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [picking, setPicking] = useState(false);
    const token = useAccessToken();
    const [forwarding, setForwarding] = useState(false);
    // The hover toolbar stays up while its "More actions" menu is open, so the menu keeps its anchor.
    const [moreOpen, setMoreOpen] = useState(false);
    const [translation, setTranslation] = useState<string | null>(null);
    const [note, setNote] = useState<string | null>(null);
    const flash = (text: string) => { setNote(text); window.setTimeout(() => setNote(null), 2500); };
    // Important / Urgent flag carried in the body; the text underneath is what gets shown and edited.
    const flag = splitImportance(message.bodyHtml);

    const chips = (reactions ?? []).filter((r) => r.messageId === message.id);
    const reactedByMe = (emoji: string) =>
        chips.some((c) => c.emoji === emoji && c.userIds.some((u) => normalizeUserId(u) === me));

    const toggle = (emoji: string) => {
        setPicking(false);
        action.mutate({ type: reactedByMe(emoji) ? "unreact" : "react", messageId: message.id, emoji });
    };

    const plainText = () => htmlToText(splitFileMarkers(flag.html).html);
    const saveForLater = async () => {
        try {
            if (scope === "dms") await connectParityApi.saveDirectMessage(containerId, message.id, token);
            else if (scope === "conversations") await connectParityApi.saveConversationMessage(containerId, message.id, token);
            else return;
            flash("Saved. Find it under Saved messages.");
        } catch {
            flash("Couldn't save the message.");
        }
    };
    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(plainText());
            flash("Copied.");
        } catch {
            flash("Couldn't copy.");
        }
    };
    const translate = async () => {
        if (translation) { setTranslation(null); return; }
        try {
            const res = await connectParityApi.translate(plainText(), (navigator.language || "en").slice(0, 2), token);
            setTranslation(res.translated);
        } catch {
            flash("Couldn't translate this message.");
        }
    };
    const quoteReply = () => {
        const text = plainText().split("\n").slice(0, 3).join("\n> ");
        window.dispatchEvent(new CustomEvent("fg:quote-message", {
            detail: { containerId, author: message.authorDisplayName || message.authorEmail || "Someone", text }
        }));
    };

    const startEdit = () => {
        // Edit the text only; the message's files are carried over untouched. Older "link" files stay in the text.
        setEditHtml(splitFileMarkers(flag.html, { legacy: false }).html);
        setEditEmpty(false);
        setConfirmDelete(false);
        setEditing(true);
    };

    const saveEdit = () => {
        const body = editor.current?.getHtml() ?? "";
        const files = fileMarkersOf(flag.html);
        if (!body && !files) return;
        action.mutate(
            { type: "edit", messageId: message.id, bodyHtml: wrapImportance(body + files, flag.importance) },
            { onSuccess: () => setEditing(false) }
        );
    };

    // Up arrow in the composer asks the newest message of yours to open for editing.
    useEffect(() => {
        if (!editable) return;
        const onEditRequest = (e: Event) => {
            if ((e as CustomEvent<{ id: string }>).detail?.id === message.id) startEdit();
        };
        window.addEventListener("fg:edit-message", onEditRequest);
        return () => window.removeEventListener("fg:edit-message", onEditRequest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editable, message.id, message.bodyHtml]);

    return (
        <div className={cn("relative", className)}>
            {flag.html.includes('data-fg-qna="1"') ? (
                <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">Question</div>
            ) : null}
            {flag.importance !== "standard" ? (
                <div
                    className={cn(
                        "mb-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white",
                        flag.importance === "urgent" ? "bg-destructive" : "bg-amber-500"
                    )}
                >
                    <AlertTriangle className="h-3 w-3" aria-hidden /> {flag.importance === "urgent" ? "Urgent" : "Important"}
                </div>
            ) : null}
            {/* The bubble holds the text (or the edit box); reactions sit under it. */}
            <div className={bubbleClassName}>
            {editing ? (
                <div className="mt-1">
                    <RichInput
                        ref={editor}
                        initialHtml={editHtml}
                        placeholder="Edit message"
                        ariaLabel="Edit message"
                        autoFocus
                        keepImages
                        enterAlwaysSubmits
                        onChange={(state) => setEditEmpty(state.empty)}
                        onSubmit={saveEdit}
                        onEscape={() => setEditing(false)}
                        footerLeft={<span className="px-1 text-[10px] text-muted-foreground">Enter to save {"·"} Esc to cancel</span>}
                        footerRight={(
                            <>
                                <button type="button" onClick={() => setEditing(false)} className="h-7 rounded-md border px-2 text-xs hover:bg-accent">
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={saveEdit}
                                    disabled={action.isPending || (editEmpty && !fileMarkersOf(flag.html))}
                                    className="flex h-7 items-center gap-1 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground disabled:opacity-50"
                                >
                                    <Check className="h-3.5 w-3.5" /> Save
                                </button>
                            </>
                        )}
                    />
                </div>
            ) : (
                <MessageBody
                    html={flag.html}
                    author={message.authorDisplayName || message.authorEmail || undefined}
                    className={cn(!bubbleClassName && "mt-0.5", "break-words text-sm text-foreground [&_a]:text-primary [&_a]:underline")}
                />
            )}
            {(message.isEdited || pin?.pinned) && !editing ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    {pin?.pinned ? <><Pin className="h-3 w-3 text-amber-500" aria-hidden />Pinned</> : null}
                    {message.isEdited && pin?.pinned ? " · " : null}
                    {message.isEdited ? "(edited)" : null}
                </span>
            ) : null}
            </div>

            {translation ? (
                <p className="mt-1 rounded-md bg-muted/60 px-2 py-1 text-xs text-muted-foreground">
                    <span className="font-medium">Translation:</span> {translation}
                </p>
            ) : null}
            {note ? <p role="status" className="mt-1 text-[11px] text-muted-foreground">{note}</p> : null}
            {forwarding ? (
                <ForwardMessageDialog
                    message={{
                        id: message.id,
                        containerId,
                        parentMessageId: null,
                        authorUserId: message.authorUserId,
                        authorEmail: message.authorEmail ?? "",
                        authorDisplayName: message.authorDisplayName ?? null,
                        bodyHtml: flag.html,
                        isEdited: Boolean(message.isEdited),
                        isPinned: false,
                        sentAt: message.sentAt ?? new Date().toISOString(),
                        editedAt: null
                    } as ChatMessage}
                    onClose={() => setForwarding(false)}
                />
            ) : null}
            {confirmDelete ? (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Delete this message for everyone?</span>
                    <button
                        type="button"
                        disabled={action.isPending}
                        onClick={() => action.mutate(
                            { type: "delete", messageId: message.id },
                            { onSuccess: () => setConfirmDelete(false) }
                        )}
                        className="rounded-md bg-destructive px-2 py-0.5 font-medium text-destructive-foreground disabled:opacity-50"
                    >
                        Delete
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-md border px-2 py-0.5 hover:bg-accent">
                        Cancel
                    </button>
                </div>
            ) : null}

            {chips.length > 0 ? (
                <div className={cn("mt-1 flex flex-wrap gap-1", mine && "justify-end")}>
                    {chips.map((r) => {
                        const minePressed = reactedByMe(r.emoji);
                        return (
                            <button
                                key={r.emoji}
                                type="button"
                                onClick={() => toggle(r.emoji)}
                                aria-pressed={minePressed}
                                title={`${r.count} ${r.count === 1 ? "person" : "people"} reacted with ${r.emoji}`}
                                className={cn(
                                    // The mock-up's reaction pill; yours takes the accent.
                                    "inline-flex items-center gap-1 rounded-full border px-[7px] py-px text-xs tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    minePressed
                                        ? "border-primary bg-[var(--connect-primary-soft)] text-primary"
                                        : "border-[var(--connect-line)] bg-card hover:bg-[var(--connect-hover)]"
                                )}
                            >
                                <span>{r.emoji}</span>
                                <span className="font-medium">{r.count}</span>
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {!editing ? (
                <div
                    role="toolbar"
                    aria-label="Message actions"
                    className={cn("absolute -top-4 right-1 z-10 hidden items-center gap-0.5 rounded-md border bg-popover p-0.5 shadow-sm group-hover:flex group-focus-within:flex", moreOpen && "flex")}
                >
                    {picking ? (
                        <>
                            {QUICK_REACTIONS.map((e) => (
                                <button
                                    key={e}
                                    type="button"
                                    onClick={() => toggle(e)}
                                    aria-label={`React with ${e}`}
                                    className="flex h-6 w-6 items-center justify-center rounded text-sm hover:bg-accent"
                                >
                                    {e}
                                </button>
                            ))}
                            <ToolbarButton label="Close reactions" onClick={() => setPicking(false)}>
                                <X className="h-3.5 w-3.5" />
                            </ToolbarButton>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => toggle("👍")}
                                aria-label="Like"
                                title="Like"
                                className="flex h-6 w-6 items-center justify-center rounded text-sm hover:bg-accent"
                            >
                                👍
                            </button>
                            <ToolbarButton label="More reactions" onClick={() => setPicking(true)}>
                                <SmilePlus className="h-3.5 w-3.5" />
                            </ToolbarButton>
                            <ToolbarButton label="Reply with a quote" onClick={quoteReply}>
                                <CornerUpLeft className="h-3.5 w-3.5" />
                            </ToolbarButton>
                            {pin ? (
                                <ToolbarButton label={pin.pinned ? "Unpin message" : "Pin message"} onClick={pin.onToggle}>
                                    {pin.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                                </ToolbarButton>
                            ) : null}
                            {editable ? (
                                <ToolbarButton label="Edit message" onClick={startEdit}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </ToolbarButton>
                            ) : null}
                            {mine ? (
                                <ToolbarButton label="Delete message" onClick={() => setConfirmDelete(true)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </ToolbarButton>
                            ) : null}
                            <DropdownMenu onOpenChange={setMoreOpen}>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        aria-label="More actions"
                                        title="More actions"
                                        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                                    >
                                        <Ellipsis className="h-3.5 w-3.5" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onSelect={() => setForwarding(true)}><Forward className="mr-2 h-4 w-4" />Forward</DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => void saveForLater()}><Bookmark className="mr-2 h-4 w-4" />Save this message</DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => void copyText()}><Copy className="mr-2 h-4 w-4" />Copy text</DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => void translate()}><Languages className="mr-2 h-4 w-4" />{translation ? "Hide translation" : "Translate"}</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function ToolbarButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
            {children}
        </button>
    );
}

/** Plain text of a message for the edit box: line breaks and block ends become newlines. */
function htmlToText(html: string): string {
    // DOMParser documents are inert — no scripts run and no images load.
    const withBreaks = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li)>/gi, "\n");
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}
